import React, { useState, useEffect, useRef } from 'react';
import { useKeycloak } from '@react-keycloak/web';

interface ActivityCategory {
  name: string;
  description: string;
  default_utbms_code?: string;
  billing_rate_multiplier?: number;
}

interface ActivityCategorySelectorProps {
  value: string;
  onChange: (category: string) => void;
  /** Called when a category with a default UTBMS code is selected */
  onUtbmsCodeSuggested?: (code: string) => void;
  label?: string;
  placeholder?: string;
  disabled?: boolean;
  className?: string;
}

const API_BASE = '';

const DEFAULT_CATEGORIES = [
  'Research',
  'Drafting',
  'Client Communication',
  'Court Appearance',
  'Trial Prep',
  'Deposition',
  'Document Review',
  'Negotiation',
  'Administrative',
];

const ActivityCategorySelector: React.FC<ActivityCategorySelectorProps> = ({
  value,
  onChange,
  onUtbmsCodeSuggested,
  label = 'Activity Category',
  placeholder = 'Select or type category...',
  disabled = false,
  className = '',
}) => {
  const { keycloak } = useKeycloak();
  const [categories, setCategories] = useState<ActivityCategory[]>([]);
  const [defaultCategories, setDefaultCategories] = useState<string[]>(DEFAULT_CATEGORIES);
  const [search, setSearch] = useState('');
  const [open, setOpen] = useState(false);
  const wrapperRef = useRef<HTMLDivElement>(null);

  // Fetch time settings for category configuration
  useEffect(() => {
    if (!keycloak?.token) return;

    const fetchSettings = async () => {
      try {
        // Use the time-entries/statistics endpoint context or a lightweight settings endpoint
        // For now, we try a dedicated endpoint; if it fails, use defaults
        const res = await fetch(`${API_BASE}/api/v1/billing/time-entries/settings`, {
          headers: { Authorization: `Bearer ${keycloak.token}` },
        });
        if (res.ok) {
          const json = await res.json();
          const data = json.data;
          if (data?.custom_activity_categories?.length) {
            setCategories(data.custom_activity_categories);
          }
          if (data?.default_activity_categories?.length) {
            setDefaultCategories(data.default_activity_categories);
          }
        }
      } catch {
        // Use defaults
      }
    };

    fetchSettings();
  }, [keycloak?.token]);

  // Close on outside click
  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (wrapperRef.current && !wrapperRef.current.contains(e.target as Node)) {
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  // Build combined options list
  const allOptions: Array<{ name: string; utbmsCode?: string }> = [
    ...categories.map((c) => ({ name: c.name, utbmsCode: c.default_utbms_code })),
    ...defaultCategories
      .filter((d) => !categories.some((c) => c.name === d))
      .map((d) => ({ name: d })),
  ];

  // Filter by search
  const filtered = allOptions.filter((opt) =>
    opt.name.toLowerCase().includes((search || value).toLowerCase()),
  );

  const handleSelect = (name: string, utbmsCode?: string) => {
    onChange(name);
    setSearch('');
    setOpen(false);
    if (utbmsCode && onUtbmsCodeSuggested) {
      onUtbmsCodeSuggested(utbmsCode);
    }
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const val = e.target.value;
    setSearch(val);
    onChange(val);
    if (!open) setOpen(true);
  };

  return (
    <div ref={wrapperRef} className={`relative ${className}`}>
      {label && (
        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
          {label}
        </label>
      )}
      <input
        type="text"
        value={open ? search || value : value}
        onChange={handleInputChange}
        onFocus={() => setOpen(true)}
        placeholder={placeholder}
        disabled={disabled}
        className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-white text-sm"
      />

      {open && filtered.length > 0 && (
        <div className="absolute z-50 mt-1 w-full max-h-48 overflow-y-auto bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-600 rounded-lg shadow-lg">
          {filtered.map((opt) => (
            <button
              key={opt.name}
              type="button"
              onClick={() => handleSelect(opt.name, opt.utbmsCode)}
              className={`w-full text-left px-3 py-2 text-sm hover:bg-lex-state-hover transition-colors ${
                opt.name === value ? 'bg-lex-accent-soft font-medium' : ''
              }`}
            >
              <span className="text-gray-700 dark:text-gray-300">{opt.name}</span>
              {opt.utbmsCode && (
                <span className="ml-2 text-xs font-mono text-lex-accent">
                  ({opt.utbmsCode})
                </span>
              )}
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

export default ActivityCategorySelector;
